/**
 * Same-origin widget iframe → parent DOM portal utility.
 *
 * When the widget iframe and the parent page share the same origin,
 * we can directly access `window.top.document` and create a container
 * element in the parent's `<body>`. React's `createPortal` then renders
 * the modal into that container, so it naturally occupies the full
 * parent viewport — no postMessage or parent-side script required.
 *
 * Because the parent page is the same SU app (same origin), Katal web
 * components are already registered in the parent's custom-element
 * registry. We selectively copy only Katal-specific stylesheets from
 * the iframe's `<head>` into the parent `<head>` to avoid global CSS
 * conflicts. This ensures the modal CSS (including `.kat-modal-learning-module`
 * overrides) renders identically without affecting the parent page's styles.
 *
 * Falls back gracefully: if we're not in an iframe or the parent is
 * cross-origin, `getParentPortalContainer` returns `null` and the
 * caller should render the modal in the iframe DOM as usual.
 */

import { WIDGET_IFRAME_FULLSCREEN_STYLES } from 'src/constants/widget';

const PORTAL_CONTAINER_ID = 'su-widget-portal-root';
const PORTAL_STYLE_ATTR = 'data-su-widget-portal-style';

/**
 * Substrings used to identify style blocks that are relevant to the
 * portal (Katal component styles and widget/portal overrides). Only
 * `<style>` tags whose `textContent` contains at least one of these
 * substrings are copied to the parent document, preventing global CSS
 * resets / base styles from leaking into the parent page.
 */
const STYLE_ALLOWLIST = ['.kat-', '.katal', 'kat-modal', 'su-widget', 'su-portal'];

/** Returns true when the current page is running inside an iframe. */
export const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access throws — that itself means we're in an iframe.
    return true;
  }
};

/**
 * Returns true when the page is in an iframe AND the parent is
 * same-origin (i.e. we can access `window.top.document`).
 */
export const isSameOriginIframe = (): boolean => {
  try {
    if (window.self === window.top) return false;
    return !!window.top?.document;
  } catch {
    return false;
  }
};

/**
 * Selectively copies Katal-specific and widget-related `<style>` and
 * `<link rel="stylesheet">` elements from the iframe `<head>` into the
 * parent `<head>`, skipping any that are already present or that don't
 * match the {@link STYLE_ALLOWLIST}. This prevents global CSS resets and
 * base styles from leaking into the parent page while ensuring Katal
 * component styles and the custom `.kat-modal-learning-module` overrides
 * render correctly in the portal.
 *
 * Script tags are NOT copied because `getParentPortalContainer` only
 * allows portalling when Katal web components are already registered in
 * the parent document. If Katal is not available, the caller falls back
 * to rendering inside the iframe where all assets are present.
 *
 * Precondition: caller has verified `isSameOriginIframe()` is true.
 */
const copyStylesToParent = (topDoc: Document): void => {
  // Skip if we've already injected styles
  if (topDoc.querySelector(`[${PORTAL_STYLE_ATTR}]`)) return;

  const iframeHead = document.head;
  const parentHead = topDoc.head;

  // Copy only Katal-specific and widget-related <style> tags to avoid
  // leaking global CSS (resets, base styles) into the parent page.
  iframeHead.querySelectorAll('style').forEach((style) => {
    const content = style.textContent || '';
    const isRelevant = STYLE_ALLOWLIST.some((token) => content.includes(token));
    if (!isRelevant) return;

    const clone = topDoc.createElement('style');
    clone.textContent = content;
    clone.setAttribute(PORTAL_STYLE_ATTR, 'true');
    parentHead.appendChild(clone);
  });

  // Copy <link rel="stylesheet"> tags
  iframeHead.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]').forEach((link) => {
    // Skip links without href - they're not useful stylesheets
    // Use .href property (absolute URL) for consistent comparison
    const { href } = link;
    if (!href) return;

    const existingLinks = Array.from(topDoc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'));
    if (existingLinks.some((l) => l.href === href)) return;

    const clone = topDoc.createElement('link');
    clone.rel = 'stylesheet';
    clone.href = link.href;
    clone.setAttribute(PORTAL_STYLE_ATTR, 'true');
    parentHead.appendChild(clone);
  });
};

/**
 * Removes portal-injected styles from the parent `<head>`.
 *
 * Precondition: caller has verified `isSameOriginIframe()` is true.
 */
const removeStylesFromParent = (topDoc: Document): void => {
  topDoc.querySelectorAll(`[${PORTAL_STYLE_ATTR}]`).forEach((el) => el.remove());
};

/**
 * Returns true when a portal container already exists in the parent
 * page's `<body>`. This is a **read-only** check with no side effects
 * — unlike {@link getParentPortalContainer}, it never creates elements
 * or copies styles. Safe to call during the React render phase.
 */
export const hasParentPortalContainer = (): boolean => {
  if (!isSameOriginIframe()) return false;
  try {
    const topDoc = window.top!.document;
    const katalAvailable = topDoc.defaultView?.customElements.get('kat-modal') !== undefined;
    if (!katalAvailable) return false;
    return topDoc.getElementById(PORTAL_CONTAINER_ID) !== null;
  } catch {
    return false;
  }
};

/**
 * Gets (or creates) a portal container `<div>` in the parent page's
 * `<body>`. Also ensures styles are copied over. Returns `null` when
 * not in a same-origin iframe, or when Katal web components are not
 * already registered in the parent (scripts loaded async won't be
 * ready in time for React to render the portalled modal).
 */
export const getParentPortalContainer = (): HTMLElement | null => {
  if (!isSameOriginIframe()) return null;

  const topDoc = window.top!.document;

  // Only portal when Katal web components are already registered in the
  // parent. If the parent doesn't have Katal (e.g. an external page on
  // the same origin), copying scripts won't help — they load async and
  // won't be ready before React renders the modal into the portal.
  // Fall back to iframe-local rendering in that case.
  const katalAvailable = topDoc.defaultView?.customElements.get('kat-modal') !== undefined;
  if (!katalAvailable) return null;

  // Ensure styles are available in parent
  copyStylesToParent(topDoc);

  let container = topDoc.getElementById(PORTAL_CONTAINER_ID);
  if (!container) {
    container = topDoc.createElement('div');
    container.id = PORTAL_CONTAINER_ID;
    // The portal container should NOT have positioning/sizing
    // Let the KatModal component handle its own positioning and overlay
    container.className = 'katal';
    topDoc.body.appendChild(container);
  }
  return container;
};

/**
 * Removes the portal container and injected styles from the parent
 * page's `<body>`. Call this on cleanup / modal close.
 */
export const removeParentPortalContainer = (): void => {
  if (!isSameOriginIframe()) return;

  const topDoc = window.top!.document;
  const container = topDoc.getElementById(PORTAL_CONTAINER_ID);
  if (container) {
    topDoc.body.removeChild(container);
  }
  removeStylesFromParent(topDoc);
};

// ── Same-origin iframe fullscreen expansion ──────────────────────────
// When the parent doesn't have Katal (so we can't portal), we expand
// the iframe element itself to cover the parent viewport. The modal
// renders inside the iframe with all its styles, but visually fills
// the entire parent page.

const IFRAME_ORIGINAL_STYLES_ATTR = 'data-su-widget-original-styles';
const IFRAME_INERT_ATTR = 'data-su-widget-inert';
const IFRAME_ARIA_ATTRS = ['role', 'aria-modal', 'title'] as const;

/**
 * Returns true when the immediate parent frame (`window.parent`) is
 * same-origin and its `document` is accessible. Unlike
 * {@link isSameOriginIframe} (which checks `window.top`), this checks
 * `window.parent` — the two differ in multi-level iframe nesting
 * (e.g. `top → frame A → frame B`).
 */
const isSameOriginParent = (): boolean => {
  try {
    if (window.self === window.parent) return false;
    return !!window.parent?.document;
  } catch {
    return false;
  }
};

/**
 * Cached reference to the parent document, captured at expand time.
 * Reused in restoreIframeSize so both style-restore and inert-removal
 * operate on the same validated reference, avoiding a second
 * `window.parent.document` access that could fail independently.
 */
let cachedParentDoc: Document | null = null;

/**
 * Finds the iframe element in the immediate parent's document that
 * contains the current window. Uses `window.parent` (not `window.top`)
 * to look in the direct parent frame.
 *
 * Works for single-level iframe nesting (immediate parent). For deeper
 * nesting where an intermediate frame is cross-origin, this returns
 * null silently.
 *
 * Returns null if not found or not accessible.
 */
const findOwnIframe = (): HTMLIFrameElement | null => {
  if (!isSameOriginParent()) return null;
  try {
    const parentDoc = window.parent.document;
    const iframes = parentDoc.querySelectorAll<HTMLIFrameElement>('iframe');
    return Array.from(iframes).find((iframe) => {
      try {
        return iframe.contentWindow === window.self;
      } catch {
        return false;
      }
    }) || null;
  } catch {
    return null;
  }
};

/**
 * Expands the iframe to cover the full parent viewport. Saves original
 * styles so they can be restored later. Marks sibling elements as inert
 * so assistive technologies cannot reach the visually-hidden parent
 * content while the modal is open (WCAG 2.1 AA SC 2.1.2 No Keyboard
 * Trap, SC 4.1.3 Status Messages).
 *
 * Call when opening a modal in the iframe-local fallback (parent has no Katal).
 */
export const expandIframeToFullscreen = (): void => {
  const iframe = findOwnIframe();
  if (!iframe) return;

  // Save original inline styles for restoration
  if (!iframe.hasAttribute(IFRAME_ORIGINAL_STYLES_ATTR)) {
    iframe.setAttribute(IFRAME_ORIGINAL_STYLES_ATTR, iframe.style.cssText);
  }

  Object.assign(iframe.style, WIDGET_IFRAME_FULLSCREEN_STYLES);

  // Add ARIA attributes so screen readers announce the iframe as a dialog
  // in the parent document (WCAG 2.1 SC 4.1.2, SC 2.4.1).
  iframe.setAttribute('role', 'dialog');
  iframe.setAttribute('aria-modal', 'true');
  iframe.setAttribute('title', 'Learning module');

  // Cache the parent document reference for use in restoreIframeSize.
  // Separated into two try-catch blocks so that if the forEach fails
  // partway through (e.g. a sibling throws on setAttribute), the cached
  // reference is preserved and restoreIframeSize can still clean up
  // partially-applied inert attributes.
  try {
    cachedParentDoc = window.parent.document;
  } catch {
    // Cross-origin or access error — cannot reach parent document
    cachedParentDoc = null;
  }

  if (cachedParentDoc) {
    try {
      // Walk from the iframe upward to <body>, marking all siblings at
      // every ancestor level as inert. This ensures that elements at
      // any nesting depth (not just direct <body> children) cannot
      // receive focus while the fullscreen iframe overlay is active
      // (WCAG 2.1 SC 2.4.3 Focus Order).
      let current: Element | null = iframe;
      while (current && current.parentElement && current.parentElement !== cachedParentDoc.body.parentElement) {
        const node: Element = current; // capture for closure safety
        Array.from(node.parentElement!.children).forEach((sibling: Element) => {
          if (sibling !== node) {
            sibling.setAttribute('inert', '');
            sibling.setAttribute(IFRAME_INERT_ATTR, 'true');
          }
        });
        current = node.parentElement;
      }
    } catch {
      // forEach failed partway — cachedParentDoc preserved so
      // restoreIframeSize can clean up partial inert attributes
    }
  }
};

/**
 * Restores the iframe to its original dimensions and removes inert
 * attributes from sibling elements. Uses the cached parent document
 * reference from expandIframeToFullscreen to avoid a second independent
 * window.parent.document access. Call when closing the modal.
 */
export const restoreIframeSize = (): void => {
  // Always clean up inert attributes regardless of iframe availability.
  // If the iframe was removed from the parent DOM while the modal was
  // open, findOwnIframe() returns null — but the parent page's content
  // would remain permanently inert without this unconditional cleanup.
  const parentDoc = cachedParentDoc;
  cachedParentDoc = null;
  if (parentDoc) {
    try {
      parentDoc.querySelectorAll(`[${IFRAME_INERT_ATTR}]`).forEach((el) => {
        el.removeAttribute('inert');
        el.removeAttribute(IFRAME_INERT_ATTR);
      });
    } catch {
      // Document may have been detached — best-effort
    }
  }

  const iframe = findOwnIframe();
  if (!iframe) return;

  const originalStyles = iframe.getAttribute(IFRAME_ORIGINAL_STYLES_ATTR);
  if (originalStyles !== null) {
    iframe.style.cssText = originalStyles;
    iframe.removeAttribute(IFRAME_ORIGINAL_STYLES_ATTR);
  }

  // Remove ARIA dialog attributes added by expandIframeToFullscreen
  IFRAME_ARIA_ATTRS.forEach((attr) => iframe.removeAttribute(attr));
};
